import React, { useEffect, useRef, useState } from "react";
import "./AgentFaceStage.css";
import { OutfitOverlay, DEFAULT_OUTFIT } from "./agentWardrobeItems.jsx";
import { attachAnalyser } from "@/lib/audioGraph";

/**
 * AgentFaceStage — full-body mascot robots inspired by the E3 mascot:
 * oval head with two glowing pill eyes, torso, two arms, jumper /
 * clothing palette per gender. Owner asked: "have arms and clothes
 * on, female pink and purple, male blue/green/gold jumpers".
 *
 * Gender + clothing assignment (per owner spec):
 *   tom    — male   — BLUE jumper
 *   sarah  — female — PINK jumper
 *   eva    — female — PURPLE jumper
 *   scout  — male   — GREEN jumper
 *   dobby  — male   — GOLD jumper
 *   jarvis — neutral — gold accent
 */

export const AGENT_PALETTES = {
  // glow = eye / accent colour. jumper = torso colour. trim = collar / cuffs.
  jarvis: { glow: "#FFB800", jumper: "#1a1a2a", trim: "#FFB800" },
  tom:    { glow: "#7DD3FC", jumper: "#1E3A8A", trim: "#7DD3FC" }, // blue
  sarah:  { glow: "#FBA0C7", jumper: "#EC4899", trim: "#FBCFE8" }, // pink
  eva:    { glow: "#C4B5FD", jumper: "#7C3AED", trim: "#DDD6FE" }, // purple
  scout:  { glow: "#86EFAC", jumper: "#15803D", trim: "#86EFAC" }, // green
  dobby:  { glow: "#FCD34D", jumper: "#B45309", trim: "#FCD34D" }, // gold
};

export function paletteFor(agentName) {
  const k = (agentName || "").toLowerCase().trim();
  return AGENT_PALETTES[k] || AGENT_PALETTES.jarvis;
}

/**
 * Renders the mascot body (head + torso + arms) — same shape for all,
 * each agent gets its own head trim + clothing palette via props.
 * `headStyle` adds per-agent flair (antenna / headphones / visor etc).
 */
function MascotBody({ palette, eye, mouth, headStyle = "default", outfit }) {
  const { glow, jumper, trim } = palette;
  // Mouth opening level (0..1) → driven by audio level so it lip-syncs.
  const mouthH = 2 + mouth * 12;   // 2px closed → 14px wide-open
  const mouthW = 14 + mouth * 10;  // gets wider as it opens
  return (
    <svg viewBox="0 0 240 320" width="100%" height="100%">
      <defs>
        <linearGradient id={`jumper-${headStyle}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={jumper} stopOpacity="1"/>
          <stop offset="100%" stopColor={jumper} stopOpacity="0.78"/>
        </linearGradient>
        <radialGradient id={`headLight-${headStyle}`} cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.18"/>
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0"/>
        </radialGradient>
      </defs>

      {/* ARMS — slight upward wave when speaking */}
      <g style={{ transformOrigin: "58px 200px", transform: `rotate(${-2 - mouth * 6}deg)` }}>
        <path d="M 58 200 Q 30 180 36 140 Q 40 110 52 102"
              fill="none" stroke={jumper} strokeWidth="14" strokeLinecap="round"/>
        <circle cx="52" cy="100" r="11" fill={trim}/>
        <circle cx="52" cy="100" r="7"  fill="#f4f4f5"/>
      </g>
      <g style={{ transformOrigin: "182px 200px", transform: `rotate(${2 + mouth * 6}deg)` }}>
        <path d="M 182 200 Q 210 180 204 140 Q 200 110 188 102"
              fill="none" stroke={jumper} strokeWidth="14" strokeLinecap="round"/>
        <circle cx="188" cy="100" r="11" fill={trim}/>
        <circle cx="188" cy="100" r="7"  fill="#f4f4f5"/>
      </g>

      {/* TORSO */}
      <path d="M 70 200 L 170 200 L 178 280 L 62 280 Z"
            fill={`url(#jumper-${headStyle})`} stroke={trim} strokeWidth="2"/>
      <path d="M 88 200 L 152 200 L 148 212 L 92 212 Z" fill={trim} opacity="0.85"/>
      <path d="M 95 230 L 145 230" stroke={trim} strokeWidth="2" opacity="0.4"/>

      <ellipse cx="120" cy="285" rx="60" ry="6" fill="#0a0a14" opacity="0.5"/>

      {/* HEAD */}
      <ellipse cx="120" cy="110" rx="62" ry="56"
               fill="#e6e7ea" stroke="#1a1a24" strokeWidth="2"/>
      <ellipse cx="120" cy="110" rx="62" ry="56" fill={`url(#headLight-${headStyle})`}/>
      <rect x="68" y="92" width="104" height="44" rx="22"
            fill="#0e0e16" stroke="#1a1a24" strokeWidth="1.5"/>
      {/* EYES — audio-reactive glow */}
      <rect x="92"  y="104" width="20" height="22" rx="10"
            fill={glow} opacity={eye}
            style={{ filter: `drop-shadow(0 0 ${4 + eye * 6}px ${glow})` }}/>
      <rect x="128" y="104" width="20" height="22" rx="10"
            fill={glow} opacity={eye}
            style={{ filter: `drop-shadow(0 0 ${4 + eye * 6}px ${glow})` }}/>

      {/* MOUTH — lip-syncs with audio level. Sits below the face plate.
          Opens vertically as the voice gets louder, like a real mouth. */}
      <rect x={120 - mouthW / 2} y={144 - mouthH / 2}
            width={mouthW} height={mouthH} rx={Math.min(6, mouthH / 2)}
            fill="#0e0e16" stroke={trim} strokeWidth="1.5"
            style={{ transition: "all 60ms linear",
                     filter: mouth > 0.2 ? `drop-shadow(0 0 4px ${glow}88)` : "none" }}/>
      {/* Inner mouth glow when open — gives it a "talking" feel */}
      {mouth > 0.15 && (
        <rect x={120 - (mouthW - 4) / 2} y={144 - (mouthH - 2) / 2}
              width={mouthW - 4} height={Math.max(0, mouthH - 2)}
              rx={Math.min(4, (mouthH - 2) / 2)}
              fill={glow} opacity={mouth * 0.35}/>
      )}

      {/* PER-AGENT HEAD FLAIR — now reacts to mouth (talking) level */}
      {headStyle === "antenna" && (
        <>
          <line x1="120" y1="55" x2="120" y2={35 - mouth * 4} stroke={trim} strokeWidth="3"/>
          <circle cx="120" cy={32 - mouth * 4} r={5 + mouth * 2} fill={trim}
                  style={{ filter: `drop-shadow(0 0 ${2 + mouth * 8}px ${glow})` }}/>
        </>
      )}
      {headStyle === "headphones" && (
        <>
          <path d="M 65 105 Q 120 50 175 105" fill="none" stroke={trim} strokeWidth="4"/>
          <ellipse cx="62" cy="110" rx={9 + mouth * 2} ry={14 + mouth * 2} fill={trim}
                   style={{ filter: `drop-shadow(0 0 ${1 + mouth * 6}px ${glow})` }}/>
          <ellipse cx="178" cy="110" rx={9 + mouth * 2} ry={14 + mouth * 2} fill={trim}
                   style={{ filter: `drop-shadow(0 0 ${1 + mouth * 6}px ${glow})` }}/>
        </>
      )}
      {headStyle === "hairtuft" && (
        <>
          <path d="M 90 70 Q 120 40 150 70" fill={trim}/>
          <path d="M 120 56 L 132 42" stroke={trim} strokeWidth="3" strokeLinecap="round"
                opacity={0.7 + mouth * 0.3}/>
        </>
      )}
      {headStyle === "cap" && (
        <>
          <path d="M 70 92 Q 120 60 170 92 L 170 86 Q 120 50 70 86 Z" fill={trim}/>
          <rect x="65" y="92" width="110" height="6" rx="3" fill={trim}/>
          {mouth > 0.3 && (
            <circle cx="170" cy="78" r={2 + mouth * 2} fill={glow}
                    style={{ filter: `drop-shadow(0 0 4px ${glow})` }}/>
          )}
        </>
      )}
      {headStyle === "horns" && (
        <>
          <path d={`M 90 64 L 96 ${44 - mouth * 4} L 100 64`} fill={trim}
                style={{ filter: `drop-shadow(0 0 ${1 + mouth * 4}px ${glow})` }}/>
          <path d={`M 140 64 L 144 ${44 - mouth * 4} L 150 64`} fill={trim}
                style={{ filter: `drop-shadow(0 0 ${1 + mouth * 4}px ${glow})` }}/>
        </>
      )}

      {/* ear / side panel lights — pulse with voice */}
      <circle cx="62"  cy="120" r={4 + mouth * 2} fill={trim} opacity={0.6 + mouth * 0.4}/>
      <circle cx="178" cy="120" r={4 + mouth * 2} fill={trim} opacity={0.6 + mouth * 0.4}/>

      {/* 👕 OUTFIT OVERLAY — owner-selected wardrobe items render on top */}
      <OutfitOverlay outfit={outfit} palette={palette}/>
    </svg>
  );
}

const HEAD_STYLE_FOR_AGENT = {
  tom: "antenna",
  sarah: "headphones",
  eva: "hairtuft",
  scout: "cap",
  dobby: "horns",
  jarvis: "default",
};

export default function AgentFaceStage({ agent, speaking, audioEl, leaving = false }) {
  const [mouthLevel, setMouthLevel] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!audioEl || !speaking) {
      setMouthLevel(0);
      return undefined;
    }
    let cancelled = false;
    try {
      const analyser = attachAnalyser(audioEl, { fftSize: 64 });
      if (!analyser) throw new Error("analyser unavailable");
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (cancelled) return;
        analyser.getByteFrequencyData(data);
        let sum = 0, n = 0;
        for (let i = 2; i < Math.min(10, data.length); i++) { sum += data[i]; n++; }
        const avg = n ? sum / n : 0;
        setMouthLevel(Math.min(1, avg / 140));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch {
      let t = 0;
      const tick = () => {
        if (cancelled) return;
        t += 0.18;
        setMouthLevel((Math.sin(t) + 1) / 2 * 0.55);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [audioEl, speaking]);

  if (!agent) return null;
  const palette = paletteFor(agent.name);
  const agentKey = (agent.name || "jarvis").toLowerCase().trim();
  const headStyle = HEAD_STYLE_FOR_AGENT[agentKey] || "default";
  const eyeGlow = 0.65 + mouthLevel * 0.35;
  // 👕 Outfit selection persisted per agent (see AgentWardrobe.jsx).
  // Falls back to DEFAULT_OUTFIT() when nothing's been picked yet.
  const outfit = (agent.outfit && typeof agent.outfit === "object")
    ? { ...DEFAULT_OUTFIT(), ...agent.outfit }
    : DEFAULT_OUTFIT();

  return (
    <div
      className={`agent-face-stage ${leaving ? "fade-out" : ""}`}
      data-testid={`agent-face-${agentKey}`}
      style={{ "--agent-primary": palette.glow, "--agent-glow": palette.glow + "AA" }}
    >
      <div className="face-inner">
        <div className="face-sway">
          <div className="robot-head-wrap" style={{ filter: `drop-shadow(0 8px 16px rgba(0,0,0,0.55)) drop-shadow(0 0 ${6 + mouthLevel * 10}px ${palette.glow}88)` }}>
            <MascotBody palette={palette} eye={eyeGlow} mouth={mouthLevel} headStyle={headStyle} outfit={outfit} />
          </div>
        </div>
        <div className="agent-name-pill" style={{ color: palette.glow }}>
          {(agent.name || "agent").toUpperCase()}
        </div>
      </div>
    </div>
  );
}
